import React, { useEffect, useState } from "react";
import {
  ArrowLeft,
  LogOut,
  User,
  History,
  Navigation,
  Pencil,
} from "lucide-react";
import { Link } from "react-router-dom";
import QRCode from "qrcode";

export default function Profile() {
  const student = JSON.parse(localStorage.getItem("student"));

  const [qrImage, setQrImage] = useState("");
  const [washesUsed, setWashesUsed] = useState(0);
  const totalWashes = 30;

  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  // FORM STATE
  const [form, setForm] = useState({
    name: student?.name || "",
    room: student?.roomNumber || student?.room || "",
    password: "",
    phone: student?.phone || "",
    email: student?.email || "",
  });

  // Generate QR
  useEffect(() => {
    if (!student) return;

    QRCode.toDataURL(student.usn).then((url) => setQrImage(url));
  }, [student]);

  // Fetch wash count
  useEffect(() => {
    if (!student) return;

    const isDemo = localStorage.getItem("demoMode") === "true";
    if (isDemo) {
      setWashesUsed(8);
      return;
    }

    fetch(`https://hostel-laundry-api.onrender.com/student/washes/${student.usn}`)

      .then((res) => res.json())
      .then((data) => {
        if (data.washesUsed !== undefined) setWashesUsed(data.washesUsed);
      })
      .catch(() => console.log("Error fetching wash count"));
  }, [student]);

  const logout = () => {
    localStorage.removeItem("student");
    localStorage.removeItem("demoMode");
    window.location.href = "/";
  };

  const washesRemaining = totalWashes - washesUsed;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  // SAVE PROFILE
  const handleSave = async () => {
    if (!student) return;

    setSaving(true);

    try {
      const res = await fetch("https://hostel-laundry-api.onrender.com/student/update",
 {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          usn: student.usn,
          name: form.name,
          room: form.room,
          password: form.password || undefined,
          phone: form.phone,
          email: form.email,
        }),
      });

      const result = await res.json();

      if (!result.success) {
        alert(result.message || "Failed to update profile");
        setSaving(false);
        return;
      }

      // Update LocalStorage
      const updated = {
        ...student,
        name: form.name,
        room: form.room,
        roomNumber: form.room,
        phone: form.phone,
        email: form.email,
        ...(form.password ? { password: form.password } : {}),
      };

      localStorage.setItem("student", JSON.stringify(updated));

      alert("Profile updated successfully!");
      setIsEditing(false);
      setSaving(false);
      window.location.reload();
    } catch (err) {
      console.error(err);
      alert("Server error");
      setSaving(false);
    }
  };

  if (!student) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <p className="text-neutral-500 text-sm">Please log in again.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white p-4 pb-14">
      {/* HEADER */}
      <div className="flex items-center justify-between bg-white border border-neutral-200 rounded-xl px-5 py-4 mb-6">
        <Link to="/student-dashboard" className="p-2 rounded-full hover:bg-neutral-100 transition">
          <ArrowLeft className="w-5 h-5 text-neutral-700" />
        </Link>

        <h1 className="text-lg font-bold text-neutral-900">Profile</h1>

        <button onClick={logout} className="p-2 rounded-full hover:bg-neutral-100 transition">
          <LogOut className="w-5 h-5 text-neutral-500" />
        </button>
      </div>

      {/* USER CARD */}
      <div className="bg-white border border-neutral-200 rounded-2xl p-7 mb-5">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-2xl bg-neutral-100 flex items-center justify-center border border-neutral-200">
              <User className="w-8 h-8 text-neutral-600" />
            </div>

            <div>
              <p className="text-xl font-bold text-neutral-900">{student.name}</p>
              <p className="text-neutral-500 text-sm">{student.usn}</p>
              <p className="text-neutral-400 text-xs mt-0.5">
                {student.hostel?.toUpperCase()} Hostel • Room{" "}
                {student.roomNumber || student.room}
              </p>
            </div>
          </div>

          <button
            onClick={() => setIsEditing((v) => !v)}
            className="px-4 py-2 bg-neutral-900 text-white rounded-lg flex items-center gap-2 text-sm font-medium hover:bg-neutral-800 transition"
          >
            <Pencil className="w-3.5 h-3.5" />
            {isEditing ? "Close" : "Edit"}
          </button>
        </div>

        {/* STATIC DISPLAY */}
        {!isEditing && (
          <div className="space-y-2.5">
            <ProfileRow label="Hostel" value={student.hostel?.toUpperCase()} />
            <ProfileRow label="Room Number" value={student.roomNumber || student.room} />
            <ProfileRow label="Phone" value={student.phone || "Not Provided"} />
            <ProfileRow label="Email" value={student.email || "Not Provided"} />
          </div>
        )}

        {/* EDIT MODE */}
        {isEditing && (
          <div className="mt-2 space-y-3">
            <EditInput label="Name" name="name" value={form.name} onChange={handleChange} />
            <EditInput label="Room Number" name="room" value={form.room} onChange={handleChange} />
            <EditInput label="Phone" name="phone" value={form.phone} onChange={handleChange} />
            <EditInput label="Email" name="email" value={form.email} onChange={handleChange} />
            <EditInput
              label="New Password (optional)"
              name="password"
              type="password"
              value={form.password}
              onChange={handleChange}
            />

            <div className="flex gap-3 pt-2">
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex-1 py-3 bg-neutral-900 text-white rounded-xl font-semibold text-sm hover:bg-neutral-800 disabled:bg-neutral-300 disabled:cursor-not-allowed transition"
              >
                {saving ? "Saving..." : "Save Changes"}
              </button>

              <button
                onClick={() => setIsEditing(false)}
                className="flex-1 py-3 bg-neutral-100 text-neutral-700 rounded-xl font-semibold text-sm hover:bg-neutral-200 transition"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      {/* QR CODE */}
      <div className="bg-white border border-neutral-200 rounded-2xl p-7 text-center mb-5">
        <h2 className="text-base font-semibold text-neutral-900 mb-4">Your QR Code</h2>

        <div
          className="w-40 h-40 mx-auto rounded-xl border border-neutral-200 overflow-hidden select-none"
          onContextMenu={(e) => e.preventDefault()}
        >
          <img src={qrImage} alt="QR Code" className="w-full h-full pointer-events-none" />
        </div>

        <p className="text-xs text-neutral-400 mt-3">Show this QR at the laundry counter</p>
      </div>

      {/* WASH STATS */}
      <div className="bg-white border border-neutral-200 rounded-2xl p-7 mb-6">
        <h2 className="text-base font-semibold text-neutral-900 mb-4">Wash Usage</h2>

        <div className="grid grid-cols-3 gap-3 text-center">
          <InfoPill title="Used" value={washesUsed} />
          <InfoPill title="Remaining" value={washesRemaining} />
          <InfoPill title="Total" value={30} />
        </div>

        <div className="mt-5 w-full bg-neutral-100 h-2 rounded-full overflow-hidden">
          <div
            className="h-2 bg-neutral-900 rounded-full transition-all duration-500"
            style={{ width: `${(washesUsed / totalWashes) * 100}%` }}
          />
        </div>
      </div>

      {/* ACTION BUTTONS */}
      <div className="space-y-3">
        <Link
          to="/track-order"
          className="w-full py-3.5 bg-neutral-900 text-white rounded-xl text-sm font-semibold hover:bg-neutral-800 flex items-center justify-center gap-2.5 transition shadow-sm"
        >
          <Navigation className="w-4 h-4" />
          Track Latest Order
        </Link>

        <Link
          to="/history"
          className="w-full py-3.5 bg-white border border-neutral-200 text-neutral-900 rounded-xl text-sm font-semibold hover:bg-neutral-50 hover:border-neutral-300 flex items-center justify-center gap-2.5 transition"
        >
          <History className="w-4 h-4" />
          View Order History
        </Link>
      </div>
    </div>
  );
}

/* COMPONENTS */

function ProfileRow({ label, value }) {
  return (
    <div className="flex justify-between bg-neutral-50 px-4 py-3 rounded-xl border border-neutral-100">
      <span className="text-neutral-500 text-sm font-medium">{label}</span>
      <span className="text-neutral-900 text-sm font-semibold">{value}</span>
    </div>
  );
}

function InfoPill({ title, value }) {
  return (
    <div className="bg-neutral-50 rounded-xl py-3 border border-neutral-100 flex flex-col items-center">
      <p className="text-xs text-neutral-400">{title}</p>
      <p className="text-lg font-bold text-neutral-900">{value}</p>
    </div>
  );
}

function EditInput({ label, ...props }) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-sm font-medium text-neutral-600">{label}</label>
      <input
        {...props}
        className="w-full px-4 py-2.5 rounded-xl border border-neutral-200 bg-neutral-50 focus:ring-2 focus:ring-neutral-900 focus:border-transparent focus:bg-white outline-none text-sm transition-all"
      />
    </div>
  );
}
